/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import animateScrollTo from 'animated-scroll-to';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from 'lodash/fp/compose';
import Spacer from '../../../ui/Spacer';
import { Icon } from '../../../ui/Icon';
import { Container, Row, Column } from '../../../../styles/Grid';
import { colorsPalette } from '../../../../styles/colors';
import Card from '../../../ui/Card';
import PromoCard from '../PromoCard';
import Tooltip from 'react-simple-tooltip';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import {
  ResultsCardsGridHeaderCss,
  spacerMarginCss,
  spacerPaddingCss,
  locationsCss,
  cardColumnCss,
  locationsDisclaimerCss,
  locationDisclaimerIconCss,
  tooltipCss,
  arrowContainerCss,
  backToTopArrowCss,
  noResultsCss,
} from './index.styles';

export const defaultData = {
  BackgroundColor: { fields: { Value: { value: 'transparent' } } },
  TotalSearchResults: null,
  DisclaimerPriceText: { value: '' },
  PriceDisclaimerTooltip: { value: '' },
  Cards: null,
  Locations: null,
  SearchNotFound: false,
};

const goToTop = () => {
  animateScrollTo(0);
};

export class ResultsGrid extends SafePureComponent {
  safeRender() {
    const { props } = this;
    const fields = defaultsDeep(props.fields, defaultData);
    const { t, locationsLabel } = props;
    const {
      BackgroundColor,
      TotalSearchResults,
      Cards,
      DisclaimerPriceText,
      PriceDisclaimerTooltip,
      Locations,
      ShowNoResults,
    } = fields;

    const listingCols = { sm: 6, md: 4, lg: 6, xl: 4 };

    return (
      <Spacer
        marginCss={spacerMarginCss}
        paddingCss={spacerPaddingCss}
        backgroundColor={BackgroundColor}
      >
        <Container>
          <Row css={ResultsCardsGridHeaderCss}>
            <Column cols={{ sm: 6, md: 4, lg: 4, xl: 4 }} css={locationsCss}>
              {ShowNoResults && <p css={noResultsCss}>{t('SearchNotFound')}</p>}
              {!ShowNoResults && TotalSearchResults !== 0 && (
                <p>{`${TotalSearchResults} ${locationsLabel}`}</p>
              )}
            </Column>
            {!!get(DisclaimerPriceText, 'value', '') && (
              <Column cols={{ sm: 6, md: 8, lg: 12, xl: 12 }}>
                <div>
                  <span css={locationsDisclaimerCss}>
                    <Text field={DisclaimerPriceText} editable={false} />
                  </span>
                  <Tooltip
                    content={get(PriceDisclaimerTooltip, 'value', '')}
                    customCss={tooltipCss}
                    background={colorsPalette.white}
                    border={colorsPalette.white}
                  >
                    <Icon
                      customCss={locationDisclaimerIconCss}
                      name="tooltip-2"
                    />
                  </Tooltip>
                </div>
              </Column>
            )}
          </Row>
          <Row>
            {Cards && Cards.length > 1 && (
              <Column css={cardColumnCss} cols={listingCols}>
                <PromoCard fields={Cards[0]} />
              </Column>
            )}
            {!isEmpty(get(Locations, 'value')) &&
              Locations.value.map((card, i) => {
                return (
                  <Column
                    css={cardColumnCss}
                    cols={listingCols}
                    key={`result-card-${i}`}
                  >
                    <Card fields={card} />
                  </Column>
                );
              })}
            {Cards && Cards.length === 1 && (
              <Column css={cardColumnCss} cols={listingCols}>
                <PromoCard fields={Cards[0]} />
              </Column>
            )}
            {Cards && Cards.length > 1 && (
              <Column css={cardColumnCss} cols={listingCols}>
                <PromoCard fields={Cards[1]} />
              </Column>
            )}
          </Row>
          <button css={arrowContainerCss} onClick={goToTop}>
            <Icon customCss={backToTopArrowCss} name="arrow-left" />
          </button>
        </Container>
      </Spacer>
    );
  }
}

ResultsGrid.defaultProps = { fields: defaultData, t: key => key };

ResultsGrid.propTypes = {
  fields: PropTypes.shape({
    BackgroundColor: PropTypes.shape({
      fields: PropTypes.shape({
        Value: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
    }),
    DisclaimerPriceText: PropTypes.shape({
      value: PropTypes.string,
    }),
    PriceDisclaimerTooltip: PropTypes.shape({
      value: PropTypes.string,
    }),
    PriceInfoText: PropTypes.shape({
      value: PropTypes.string,
    }),
    TotalSearchResults: PropTypes.number,
    Cards: PropTypes.arrayOf(PropTypes.object),
    Locations: PropTypes.shape({ value: PropTypes.arrayOf(PropTypes.object) }),
  }),
  locationsLabel: PropTypes.string,
};

ResultsGrid.displayName = 'ResultsCards/ResultsGrid';

export default compose(withNamespaces())(ResultsGrid);
